import { FormInput } from "@general-backoffice/core";
import type { TaxProfile } from "@investhinks/api-client";
import React, { useMemo } from "react";
import { Alert } from "reactstrap";

import IAECodeSelector from "../IAECodeSelector";
import { buildActivityTypeOptions } from "../taxProfileConstants";

type ActivityStepTaxProfile = Pick<TaxProfile, "activityType" | "iaeCode"> & Record<string, unknown>;

interface ActivityStepProps {
  taxProfile: ActivityStepTaxProfile | null;
  handleInputChange: (name: string, value: unknown) => void;
  handleInputEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  t: (key: string, fallback?: string) => string;
  tProfile: (key: string, fallback?: string | Record<string, unknown>) => string;
  iaeActivityType: string | null;
  setIaeActivityType: (type: string | null) => void;
}

const ActivityStep = ({
  taxProfile,
  handleInputChange,
  isLoading,
  t,
  tProfile,
  iaeActivityType,
  setIaeActivityType,
}: ActivityStepProps) => {
  const activityOptions = useMemo(() => buildActivityTypeOptions(t), [t]);

  const selectedActivity = activityOptions.find((o) => o.value === taxProfile?.activityType);

  return (
    <div>
      <h5 className="mb-3">{t("steps.activity.title", "Activity Information")}</h5>
      <p className="text-muted mb-4">
        {t("steps.activity.description", "Please provide your business activity details.")}
      </p>

      <IAECodeSelector
        label={t("steps.activity.iaeLabel", "IAE Code")}
        value={taxProfile?.iaeCode}
        onChange={(code) => handleInputChange("iaeCode", code)}
        onCodeSelect={(codeInfo) => {
          if (codeInfo) {
            // Auto-fill description
            handleInputChange("iaeDescription", codeInfo.description);
            // Auto-set activity type based on IAE section and track it
            if (codeInfo.activityType) {
              handleInputChange("activityType", codeInfo.activityType);
              setIaeActivityType(codeInfo.activityType);
            }
          } else {
            setIaeActivityType(null);
          }
        }}
        isDisabled={isLoading}
        placeholder={t("steps.activity.searchIae", "Search IAE code...")}
      />

      <FormInput.Select
        label={t("steps.activity.typeLabel", "Activity Type")}
        name="activityType"
        value={selectedActivity}
        options={activityOptions}
        onChange={({ value }) => handleInputChange("activityType", value)}
        isDisabled={isLoading}
      />

      {iaeActivityType && taxProfile?.activityType && taxProfile.activityType !== iaeActivityType && (
        <Alert color="warning" className="mt-2">
          <i className="fas fa-exclamation-triangle mr-2" />
          {tProfile("activityTypeMismatch", {
            expected: tProfile(`activityTypes.${iaeActivityType}`, iaeActivityType),
          })}
        </Alert>
      )}
    </div>
  );
};

export default ActivityStep;
